import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlatformSettings } from '@/lib/platformSettings';
import { useAuth } from '@/lib/auth';
import {
  initAnalytics, trackPageview, disableAnalytics,
  getConsent, CONSENT_KEY,
} from '@/lib/analytics';

/**
 * AnalyticsProvider — bridges platform_settings + auth + react-router into
 * the pure analytics helper. Mounts once at the top of the app tree.
 *
 * Responsibilities:
 *   1. Boot the providers (GA4 + Meta Pixel) exactly once when:
 *      - platform_settings has loaded with valid IDs
 *      - the current auth state is known (so staff-exclusion is honoured)
 *      - the visitor has either accepted consent OR consent isn't required
 *   2. Emit a page_view on every SPA route change (after boot).
 *   3. Re-evaluate on consent change (custom `indusfact:consent-change` event).
 *
 * This component renders nothing.
 */
export const AnalyticsProvider = () => {
  const location = useLocation();
  const { loading, analytics } = usePlatformSettings();
  const { user, loading: authLoading } = useAuth();
  const bootedRef = useRef(false);

  // (Re)boot analytics whenever config or role or consent changes.
  useEffect(() => {
    if (loading || authLoading) return;
    const cfg = analytics || {};
    const ga4 = (cfg.ga4_measurement_id || '').trim();
    const pixel = (cfg.meta_pixel_id || '').trim();
    const trackingEnabled = cfg.tracking_enabled !== false; // default true
    const excludeStaff = cfg.exclude_staff !== false;       // default true
    const consentRequired = cfg.consent_required !== false; // default true

    const isStaff = !!(user && (user.role === 'admin' || user.role === 'moderator'));
    const consent = getConsent();
    const hasConsent = !consentRequired || consent === 'granted';

    if (!ga4 && !pixel) return; // Nothing configured yet — silent no-op
    if (!hasConsent) { disableAnalytics(); return; }

    initAnalytics({ ga4, pixel, excludeStaff, isStaff, trackingEnabled });
    bootedRef.current = true;
    // Fire an initial pageview once init completes.
    trackPageview();
  }, [loading, authLoading, analytics, user]);

  // Listen for consent changes so accepting the banner takes effect immediately.
  useEffect(() => {
    const handler = () => {
      // Trigger the boot effect again by touching a state — simplest way
      // is to dispatch a storage event which the effect above ignores;
      // instead we just force re-run via location change. Cheap enough.
      // The effect above has `analytics`/`user`/`consent` as sources but
      // reads consent via getConsent() at runtime, so calling initAnalytics
      // here is sufficient after granting.
      const cfg = analytics || {};
      const ga4 = (cfg.ga4_measurement_id || '').trim();
      const pixel = (cfg.meta_pixel_id || '').trim();
      if (!ga4 && !pixel) return;
      const isStaff = !!(user && (user.role === 'admin' || user.role === 'moderator'));
      initAnalytics({
        ga4, pixel,
        excludeStaff: cfg.exclude_staff !== false,
        isStaff,
        trackingEnabled: cfg.tracking_enabled !== false,
      });
      trackPageview();
    };
    window.addEventListener('indusfact:consent-change', handler);
    // Also react to cross-tab consent grants via storage events.
    const storageHandler = (e) => { if (e.key === CONSENT_KEY) handler(); };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('indusfact:consent-change', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, [analytics, user]);

  // SPA route-change page views (after initial boot).
  useEffect(() => {
    if (!bootedRef.current) return;
    trackPageview();
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsProvider;
